import type * as React from 'react'
import markOnLight from '../../assets/logo-mark-on-light.png'
import markReverse from '../../assets/logo-mark-reverse.png'
import { cn } from '../../lib/utils'

/**
 * The Multaqa mark, and optionally the wordmark beside it — the rail header in
 * 6c, 6e, 6f and 6g.
 *
 * Two files, not one recoloured with a filter. The mark is two-tone: neutral
 * strokes plus a fixed brand red at the centre. A `filter: invert()` that
 * lightens the strokes for a dark rail also drags the red round the wheel to
 * cyan, so the theme swap has to be two assets. They are swapped in CSS rather
 * than JS — `dark:` is the same signal the tokens use, so the mark can never
 * disagree with the surface it is sitting on, including on the first paint
 * before any script has run.
 *
 * The wordmark is type, not artwork: "MULTAQA" at 11.5px semibold on 0.2em
 * tracking with "ERP" one step down in weight and colour, exactly as 6c sets
 * it. Keeping it as text means it translates, scales with the type ramp and
 * costs nothing to load.
 */
function Logo({
  className,
  size = 22,
  wordmark = false,
  tone = 'auto',
  ...props
}: React.ComponentProps<'div'> & {
  size?: number
  wordmark?: boolean
  /**
   * `reverse` always shows the light-on-dark mark, for a surface that is dark
   * in both themes: the basalt spine and the committed surface.
   */
  tone?: 'auto' | 'reverse'
}) {
  return (
    <div data-slot="logo" className={cn('flex items-center gap-[9px]', className)} {...props}>
      {/*
       * Exactly one of the two is `display: none` at any time, so only one
       * reaches the accessibility tree — no duplicate announcement. When the
       * wordmark is rendered the images become decorative and the text carries
       * the name, otherwise the visible image is the only thing that can.
       */}
      <img
        src={markOnLight}
        alt={wordmark ? '' : 'Multaqa'}
        height={size}
        style={{ height: size }}
        className={cn('block w-auto dark:hidden', tone === 'reverse' && 'hidden')}
      />
      <img
        src={markReverse}
        alt={wordmark ? '' : 'Multaqa'}
        height={size}
        style={{ height: size }}
        className={cn('hidden w-auto dark:block', tone === 'reverse' && 'block')}
      />
      {wordmark ? (
        <div className="text-foreground text-[11.5px] font-semibold tracking-[0.2em]">
          {/* biome-ignore lint/plugin: the brand wordmark, the same in every language */}
          MULTAQA <span className="text-muted-foreground font-medium">ERP</span>
        </div>
      ) : null}
    </div>
  )
}

export { Logo }
